import { z } from 'zod';
import { issue } from '@/lib/forms/messages';
import { codePointLength } from '@/lib/forms/primitives';

/**
 * Document tag editing (DocumentTagsDialog).
 *
 * A module-level const, not a `createSchema(t)` factory: the message resolves
 * lazily at parse time, so one schema serves both languages.
 *
 * THE CAP IS A MIRROR, not a product choice —
 *   database/migrations/005_metadata_tagging.sql:11  tags.name VARCHAR(100)
 *   api/routes/documents.py:1241                     `[t.strip()[:100] for t in body.tags ...]`
 * `UpdateDocumentTagsRequest` types `tags` as a bare `list[str]`, and the route
 * SILENTLY truncates each entry to 100 characters before insert. Checking it
 * here turns a silent server-side truncation into visible field feedback, which
 * is the one behaviour this migration deliberately changes; the boundary itself
 * is unchanged.
 *
 * `.trim()` before measuring matches the route's `t.strip()[:100]` — it strips
 * first, so surrounding whitespace never counts toward the 100.
 *
 * Measured in CODE POINTS (`codePointLength`), because that is the unit on both
 * sides: the route's slice is a Python str slice, and VARCHAR(100) admits 100
 * code points regardless of their byte or UTF-16 width. Plain `.length` would
 * score a 51-emoji tag as 102 and reject a tag the column stores unchanged.
 *
 * DELIBERATELY ABSENT:
 * - No cap on the NUMBER of tags. Nothing in the route, the Pydantic model or
 *   the schema bounds it, so any number here would be an invented rule.
 * - No `min(1)` on the array. The route documents 「空配列で全削除」 and clearing
 *   every tag is a valid save today.
 * - No blank-entry rule. The route drops blanks, but TagInput cannot produce
 *   one (`addTag` returns early on an empty draft), so a rule here would be
 *   unreachable.
 */
export const TAG_MAX_LENGTH = 100;

export const documentTagsSchema = z
  .object({
    tags: z.array(z.string()),
  })
  // The issue is raised on `tags`, not on `tags.N`: an error at an element path
  // lands in `errors.tags[N]`, where the field-level `<FormMessage>` reads
  // `errors.tags.message` and finds nothing — the message would never render.
  .superRefine((value, ctx) => {
    if (value.tags.some((tag) => codePointLength(tag.trim()) > TAG_MAX_LENGTH)) {
      ctx.addIssue(issue('documents.tagTooLong', ['tags'], { max: TAG_MAX_LENGTH }));
    }
  });

export type DocumentTagsFormValues = z.infer<typeof documentTagsSchema>;
